import type { ReactNode } from "react";
import { useState } from "react";
import { XIcon } from "@phosphor-icons/react";

import { toast } from "@/client/hooks/use-toast";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SOFT PROMPT CARD — carte partagée des invitations en-app
 * ─────────────────────────────────────────────────────────────────────────────
 *  Design system unique pour les prompts d'activation (push, e-mail, PWA) :
 *
 *   1. Pré-contexte : le « pourquoi » expliqué en une phrase (valeur vécue,
 *      pas une demande abstraite) — le taux d'acceptation passe de 12 % à 30 %
 *      quand le contexte précède l'action (web.dev).
 *   2. Action principale : CTA brandé MonCVPro, un clic.
 *   3. Action secondaire : « Plus tard » (cooldown long, jamais un refus).
 *   4. Fermeture (X) : cooldown plus long, ou refus définitif si `onDecline`.
 *
 *  Chartes :
 *   - light mode, tokens `surface` / `text` / `primary` (cohérent app) ;
 *   - aucun emoji, aucun ASCII art, ton professionnel et chaleureux ;
 *   - responsive : `w-[min(92vw,380px)]`, testé mobile-first.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type SoftPromptCardProps = {
  toastId: string | number;
  /** Icône dans la pastille (phosphor). */
  icon: ReactNode;
  /** Titre accrocheur (une ligne). */
  title: string;
  /** Pré-contexte : explique la valeur avant de demander l'action. */
  description: string;
  /** Libellé du CTA principal. */
  actionLabel: string;
  /** Libellé de l'action secondaire (« Plus tard »). */
  laterLabel?: string;
  /** Refus définitif optionnel (mémorisé, re-proposition sur changement de relation). */
  declineLabel?: string;
  onAction: () => Promise<void> | void;
  onLater: () => void;
  onDecline?: () => void;
  onClose: () => void;
};

export const SoftPromptCard = ({
  toastId,
  icon,
  title,
  description,
  actionLabel,
  laterLabel = "Plus tard",
  declineLabel,
  onAction,
  onLater,
  onDecline,
  onClose,
}: SoftPromptCardProps) => {
  const [busy, setBusy] = useState(false);

  const handleAction = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onAction();
    } finally {
      setBusy(false);
      toast.dismiss(toastId);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative flex w-[min(92vw,380px)] items-start gap-3 overflow-hidden rounded-2xl border border-border-subtle bg-surface/95 p-4 shadow-[0_16px_48px_-12px_rgba(20,14,44,0.18)] backdrop-blur-xl"
    >
      {/* Halo décoratif discret, aligné sur la couleur d'action de l'app */}
      <div className="pointer-events-none absolute -left-12 -top-14 size-36 rounded-full bg-primary/10 blur-2xl" />

      {/* Pastille icône */}
      <div className="relative grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </div>

      {/* Contenu : pré-contexte puis actions */}
      <div className="relative min-w-0 flex-1">
        <p className="text-sm font-bold leading-snug text-text">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-text-muted">{description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleAction()}
            className="inline-flex h-8 items-center justify-center rounded-lg bg-primary px-3.5 text-xs font-bold text-white shadow-sm transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
          >
            {actionLabel}
          </button>

          <button
            type="button"
            onClick={onLater}
            className="inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-semibold text-text-muted transition hover:bg-canvas-subtle hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            {laterLabel}
          </button>

          {declineLabel && onDecline ? (
            <button
              type="button"
              onClick={onDecline}
              className="inline-flex h-8 items-center justify-center rounded-lg px-2 text-xs font-medium text-text-muted underline-offset-2 transition hover:text-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              {declineLabel}
            </button>
          ) : null}
        </div>
      </div>

      {/* Fermeture */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="relative grid size-7 shrink-0 place-items-center rounded-md text-text-muted transition-colors hover:bg-canvas-subtle hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};
